import { gsap } from 'gsap'
import { createGroupRefs } from 'services/helpers'
import useTimeline from 'hooks/useTimeline'
import useSplitText from 'hooks/useSplitText'
import useResizeIndex from 'hooks/useResizeIndex'
import useComponentSize from '@rehooks/component-size'

const useIconTitleTextAnimation = (
  { componentRef, elementRef, titleRef, textRef, iconRef },
  { animated = true, index = 0, linesClass } = {}
) => {
  const { width, height } = useComponentSize(componentRef)
  const resizeIndex = useResizeIndex()

  const titleSplitText = useSplitText(
    titleRef,
    {
      type: 'lines',
      linesClass,
    },
    [width, height, resizeIndex]
  )
  const textSplitText = useSplitText(
    textRef,
    {
      type: 'lines',
      linesClass,
    },
    [width, height, resizeIndex]
  )

  const tl = useTimeline(
    {
      paused: true,
      delay: 0.2 * index,
      restoreTime: true,
    },
    (tl) => {
      if (!animated) return

      gsap.set(elementRef.current, {
        opacity: 1,
      })
      const stagger = 0.045
      const lines = createGroupRefs([
        [titleSplitText.current, titleSplitText.current.lines],
        [textSplitText.current, textSplitText.current.lines],
      ])

      if (lines.length > 0) {
        tl.fromTo(
          lines,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            ease: 'none',
            stagger,
            duration: 0.2,
          }
        )

        tl.fromTo(
          lines,
          {
            z: -60,
            y: 40,
          },
          {
            z: 0,
            y: 0,
            stagger,
            duration: 0.8,
            ease: 'elastic.out(0.75, 0.8)',
          },
          '<'
        )
      }

      if (iconRef.current) {
        tl.fromTo(
          iconRef.current,
          { opacity: 0 },
          { opacity: 1, ease: 'none', duration: 0.1 },
          '<0.25'
        )

        tl.fromTo(
          iconRef.current,
          {
            z: -400,
          },
          {
            z: 0,
            transformPerspective: 1000,
            duration: 0.9,
            ease: 'elastic.out(0.4, 0.2)',
          },
          '<'
        )
      }
    },
    [width, height, resizeIndex]
  )

  return tl
}

export default useIconTitleTextAnimation
